import * as React from "react";
import { FieldError } from "react-hook-form";
import {
  Input,
  Stack,
  Text,
  Select,
  BoxProps,
  InputGroup,
} from "@chakra-ui/react";

import RStack from "components/Chakra/ResponsiveGrid";
import { TranslationKeys } from "utils/localization";
import { useLocalization } from "hooks/useLocalization";
import { TInputSize } from "utils/css/types";
import SafeDate from "utils/SafeDate";
import useViewport from "hooks/useViewport";

interface IProps extends BoxProps {
  name: string;
  size?: TInputSize;
  isActive?: boolean;
  label?: string;
  value?: string;
  error?: FieldError;
  customMessage?: React.ReactNode;
  required?: boolean;
  customError?: TranslationKeys;
  onDateSelect?: (date: string) => void;
}

const DatePicker: React.FunctionComponent<IProps> = ({
  label,
  isActive = true,
  customMessage,
  required,
  value,
  customError,
  error,
  onDateSelect,
}) => {
  const { translate } = useLocalization();
  const de = new SafeDate(value || "");
  const [day, setDay] = React.useState(de.day || "");
  const [month, setMonth] = React.useState(de.month > 0 ? de.month : "");
  const [year, setYear] = React.useState(de.year || "");

  const { isMobile } = useViewport();

  const monthOptions = React.useMemo(
    () => [
      {
        value: 1,
        label: isMobile ? "1" : "Leden",
        dataTest: "january",
      },
      {
        value: 2,
        label: isMobile ? "2" : "Únor",
        dataTest: "february",
      },
      {
        value: 3,
        label: isMobile ? "3" : "Březen",
        dataTest: "march",
      },
      {
        value: 4,
        label: isMobile ? "4" : "Duben",
        dataTest: "april",
      },
      {
        value: 5,
        label: isMobile ? "5" : "Květen",
        dataTest: "may",
      },
      {
        value: 6,
        label: isMobile ? "6" : "Červen",
        dataTest: "june",
      },
      {
        value: 7,
        label: isMobile ? "7" : "Červenec",
        dataTest: "july",
      },
      {
        value: 8,
        label: isMobile ? "8" : "Srpen",
        dataTest: "august",
      },
      {
        value: 9,
        label: isMobile ? "9" : "Září",
        dataTest: "september",
      },
      {
        value: 10,
        label: isMobile ? "10" : "Říjen",
        dataTest: "october",
      },
      {
        value: 11,
        label: isMobile ? "11" : "Listopad",
        dataTest: "november",
      },
      {
        value: 12,
        label: isMobile ? "12" : "Prosinec",
        dataTest: "december",
      },
    ],
    [isMobile],
  );

  React.useEffect(() => {
    if (
      typeof year === "number" &&
      typeof month === "number" &&
      typeof day === "number"
    ) {
      const d = new SafeDate({ year, month, day });
      onDateSelect && onDateSelect(d.formatDateAsISO());
      // setValue(name, d.formatDateAsISO());
    } else {
      onDateSelect && onDateSelect("");

      // setValue(name, "");
    }
  }, [year, month, day]);

  const handleYearChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const yearValue = Number.parseInt(event.target.value, 10);
      setYear(Number.isNaN(yearValue) ? "" : yearValue);
    },
    [],
  );

  const handleMonthChange = React.useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const monthValue = Number.parseInt(event.target.value, 10);
      setMonth(Number.isNaN(monthValue) ? "" : monthValue);
    },
    [],
  );

  const handleDayChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const dayValue = Number.parseInt(event.target.value, 10);
      setDay(Number.isNaN(dayValue) ? "" : dayValue);
    },
    [],
  );

  const getColor = () => {
    if (error) {
      return "ud-error";
    }
    if (isActive) {
      return "ud-grey-600";
    }

    return "ud-grey-300";
  };

  const getBorderColor = () => {
    if (error) {
      return "ud-error";
    }
    if (isActive) {
      return "ud-grey-300";
    }

    return "ud-grey-200";
  };

  return (
    <Stack
      direction="column"
      justify="start"
      spacing="0"
      data-test="global.dateBox.div"
    >
      {label && (
        <Text
          variant="form-label"
          color={getColor()}
          data-test="global.dateBox.text"
        >
          {label}
          {required && (
            <Text as="span" pl="3px" color="ud-error">
              *
            </Text>
          )}
        </Text>
      )}
      <InputGroup
        display="block"
        border="1px"
        borderColor={getBorderColor()}
        borderRadius="md"
        _focus={{ borderColor: "ud-primary" }}
        mt="0px"
        h="40px"
      >
        <RStack
          gridTemplateColumns="1.6fr 3fr 1.8fr"
          mobile={{ gridTemplateColumns: "1.6fr 1.6fr 1.8fr" }}
          gridRowGap="3px"
        >
          <Input
            // {...registerWithError("area")}
            value={day}
            disabled={!isActive}
            placeholder="Den"
            fontWeight="normal"
            type="number"
            border="none"
            color="ud-grey-600"
            onInput={(e) => {
              if (Number(e.currentTarget.value) > 31) {
                e.currentTarget.value = new Date().getDate().toString();
              }
            }}
            onChange={handleDayChange}
            _focus={{ border: "none" }}
            data-test="offer.availability.dateDay"
          />
          <Select
            // {...registerWithError("area")}
            value={month}
            disabled={!isActive}
            fontWeight="normal"
            border="none"
            color="ud-grey-600"
            onChange={handleMonthChange}
            _focus={{ border: "none" }}
            data-test="offer.availability.dateMonthSelect"
          >
            {monthOptions.map((d) => (
              <option
                key={d.value}
                value={d.value}
                data-test={"offer.availablility.dateMonthSelect." + d.dataTest}
              >
                {d.label}
              </option>
            ))}
          </Select>
          <Input
            // {...registerWithError("area")}
            value={year}
            disabled={!isActive}
            placeholder="Rok"
            fontWeight="normal"
            type="number"
            border="none"
            color="ud-grey-600"
            onInput={(e) => {
              if (Number(e.currentTarget.value) > 2030) {
                e.currentTarget.value = new Date().getFullYear().toString();
              }
            }}
            onChange={handleYearChange}
            _focus={{ border: "none" }}
            data-test="offer.availability.dateYear"
          />
        </RStack>
      </InputGroup>

      {error?.message && (
        <Text
          variant="form-error"
          pt="4px"
          data-test="offer.availability.warningText"
        >
          {customError ? translate(customError) : error.message}
        </Text>
      )}
      {customMessage && (
        <Text variant="form-message" mt="2px">
          {customMessage}
        </Text>
      )}
    </Stack>
  );
};

export default DatePicker;
